
import { Link } from 'react-router-dom';

export default function ReinforcementPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-violet-50 to-indigo-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-600 text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link to="/motivation" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
              <i className="ri-arrow-left-line mr-2"></i>
              動機付け一覧に戻る
            </Link>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl mb-6">
              <i className="ri-repeat-line text-4xl"></i>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">強化理論</h1>
            <p className="text-xl text-white/90">Reinforcement Theory</p>
            <p className="text-lg text-white/80 mt-2">提唱者：Skinner</p>
          </div>
        </div>
      </section>

      {/* Core Message */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-purple-500 to-violet-500 rounded-2xl shadow-2xl p-8 md:p-12 text-white text-center">
              <h2 className="text-3xl font-bold mb-4">人は「強化された行動」を繰り返す</h2>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-12">
            {/* 理論の概要 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-purple-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-violet-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-book-open-line text-xl text-white"></i>
                </div>
                理論の概要
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                強化理論は、行動の結果（報酬や罰）によって、その行動が繰り返されるかどうかが決まるという理論です。ポジティブな結果（強化）を受けた行動は繰り返され、ネガティブな結果を受けた行動は減少します。
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6 border border-purple-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-add-circle-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">正の強化</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    望ましい行動に対して報酬を与え、その行動を増やす
                  </p>
                  <p className="text-sm text-purple-600 mt-2">例：良い対応をした職員を褒める</p>
                </div>
                <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-xl p-6 border border-violet-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-violet-600 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-subtract-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">負の強化</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    不快な状況を取り除くことで、望ましい行動を増やす
                  </p>
                  <p className="text-sm text-violet-600 mt-2">例：目標達成で報告義務を軽減</p>
                </div>
              </div>
            </div>

            {/* 薬局運営への活用 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-purple-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-hospital-line text-xl text-white"></i>
                </div>
                薬局運営への活用
              </h2>
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-chat-smile-3-line text-purple-600 mr-2"></i>
                    褒め方・評価コメント設計
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    「何を褒めるか」によって、職員の行動が変わります。褒め方を意識的に設計することが重要です。
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-purple-100">
                    <p className="text-sm text-gray-700">
                      <strong className="text-purple-600">ポイント：</strong>褒める行動を明確にし、一貫性を持って強化することで、望ましい行動が定着する
                    </p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-xl p-6 border border-violet-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-scales-2-line text-violet-600 mr-2"></i>
                    結果褒め vs 行動・判断褒めの使い分け
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <div className="bg-white rounded-lg p-4 border border-purple-100">
                      <h4 className="font-bold text-gray-900 mb-2 text-sm flex items-center">
                        <i className="ri-trophy-line text-purple-500 mr-2"></i>
                        結果を褒める
                      </h4>
                      <p className="text-sm text-gray-600 mb-2">
                        「売上が上がったね」「件数が増えたね」
                      </p>
                      <div className="p-2 bg-purple-50 rounded">
                        <p className="text-xs text-purple-700">
                          <strong>効果：</strong>短期的なモチベーション向上<br />
                          <strong>注意：</strong>結果が出ないと落ち込む
                        </p>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-violet-100">
                      <h4 className="font-bold text-gray-900 mb-2 text-sm flex items-center">
                        <i className="ri-lightbulb-line text-violet-500 mr-2"></i>
                        行動・判断を褒める
                      </h4>
                      <p className="text-sm text-gray-600 mb-2">
                        「あの対応は素晴らしかった」「良い判断だった」
                      </p>
                      <div className="p-2 bg-violet-50 rounded">
                        <p className="text-xs text-violet-700">
                          <strong>効果：</strong>行動の再現性が高まる<br />
                          <strong>推奨：</strong>成長につながりやすい
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 強化のタイミング */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-purple-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-violet-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-time-line text-xl text-white"></i>
                </div>
                強化のタイミング
              </h2>
              <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6 border border-purple-200">
                <p className="text-gray-700 leading-relaxed mb-4">
                  強化は<strong className="text-purple-600">タイミングが重要</strong>です。行動の直後に強化することで、効果が高まります。
                </p>
                <div className="space-y-3">
                  <div className="flex items-center p-3 bg-white rounded-lg border border-purple-100">
                    <i className="ri-checkbox-circle-fill text-green-500 mr-3"></i>
                    <span className="text-gray-700 text-sm">良い行動を見たら、その場で褒める</span>
                  </div>
                  <div className="flex items-center p-3 bg-white rounded-lg border border-purple-100">
                    <i className="ri-checkbox-circle-fill text-green-500 mr-3"></i>
                    <span className="text-gray-700 text-sm">週次・月次ではなく、日常的にフィードバックする</span>
                  </div>
                  <div className="flex items-center p-3 bg-white rounded-lg border border-purple-100">
                    <i className="ri-checkbox-circle-fill text-green-500 mr-3"></i>
                    <span className="text-gray-700 text-sm">具体的な行動を指摘して褒める</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 明日の一手 */}
            <div className="bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-600 rounded-2xl shadow-2xl p-8 text-white">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <i className="ri-calendar-check-line text-3xl mr-3"></i>
                明日の一手
              </h2>
              <p className="text-lg text-white/90 leading-relaxed mb-6">
                職員の良い行動を見つけたら、その場で具体的に褒めてみましょう。
              </p>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6">
                <p className="text-white font-semibold mb-2">具体的なアクション：</p>
                <p className="text-white/90 leading-relaxed">
                  「〇〇さん、さっきの患者さんへの説明、とても分かりやすかったですね。特に〇〇の部分が良かったです」と、行動を具体的に褒める。
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-8 border-t border-gray-200">
              <Link to="/motivation/goal-setting" className="flex items-center text-purple-600 hover:text-purple-700 font-semibold transition-colors">
                <i className="ri-arrow-left-line mr-2"></i>
                目標設定理論
              </Link>
              <Link to="/motivation/feedback" className="flex items-center text-purple-600 hover:text-purple-700 font-semibold transition-colors">
                フィードバック理論
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
